import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { PaymentForm } from "@/components/payment/PaymentForm";
import { BillingForm } from "@/components/payment/BillingForm";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { format } from "date-fns";
import { Calendar, Users, MapPin, Home } from "lucide-react";
import { initiateCustomAdventurePayment } from "@/utils/endUserAppApi";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface CustomAdventurePaymentData {
  requestNumber: string;
  adventureName: string;
  businessName: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  finalPrice: {
    total: number;
    currency: string;
    breakdown?: {
      basePrice: number;
      options: number;
      taxes?: number;
    };
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  selectedOptions: Record<string, any>;
}

const CustomAdventurePayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.payment' });
  const { t: tCommon } = useTranslation('common');
  
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const adventureData = location.state?.adventureData as CustomAdventurePaymentData;

  useEffect(() => {
    if (!adventureData) {
      toast({
        title: t('error.invalidAccess.title'),
        description: t('error.invalidAccess.description'),
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    const initializePayment = async () => {
      if (!showPaymentForm) return;
      
      try {
        console.log('Initializing custom adventure payment for:', adventureData.requestNumber);
        
        // Use the dedicated custom adventure payment initiation
        const response = await initiateCustomAdventurePayment({
          requestNumber: adventureData.requestNumber,
          amount: adventureData.finalPrice.total,
          paymentMethod: 'stripe'
        });
        
        setClientSecret(response.clientSecret);
        
      } catch (error) {
        console.error('Payment initialization error:', error);
        toast({
          title: t('error.initialization.title'),
          description: t('error.initialization.description'),
          variant: "destructive",
        });
      }
    };

    initializePayment();
  }, [adventureData, showPaymentForm, t, toast, navigate]);

  const getTotalParticipants = () => {
    return adventureData.participants.adults + adventureData.participants.children + adventureData.participants.students;
  };

  const handlePaymentSuccess = () => {
    // Show success toast
    toast({
      title: t('success.title'),
      description: t('success.description'),
      variant: "default",
    });
    
    // Navigate to final confirmation page
    navigate(`/custom-adventure-final-confirmation/${adventureData.requestNumber}`, {
      state: { 
        adventureData: {
          ...adventureData,
          paymentCompleted: true,
          paymentDate: new Date().toISOString()
        }
      }
    });
  };

  if (!adventureData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid Access</h1>
          <p className="text-gray-600 mb-4">Unable to load payment information.</p>
          <Button onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F0] py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="mb-4 bg-white hover:bg-gray-50"
          >
            ← {tCommon('actions.back')}
          </Button>
          <h1 className="text-3xl font-bold mb-2 text-gray-900">{t('header.title')}</h1>
          <p className="text-gray-600">
            {t('header.subtitle', { requestNumber: adventureData.requestNumber })}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Form */}
          <div className="lg:col-span-2">
            {!showPaymentForm ? (
              <BillingForm 
                onSubmit={() => setShowPaymentForm(true)}
                isProcessing={isProcessing}
              />
            ) : clientSecret && clientSecret !== 'mock_client_secret_for_custom_adventure_development' ? (
              <Elements 
                stripe={stripePromise} 
                options={{
                  clientSecret,
                  appearance: { theme: 'stripe' }
                }}
              >
                <PaymentForm 
                  amount={adventureData.finalPrice.total}
                  bookingId={adventureData.requestNumber}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  onSuccess={handlePaymentSuccess}
                  onError={(message) => {
                    toast({
                      title: t('error.processing.title'),
                      description: message,
                      variant: "destructive",
                    });
                  }}
                  returnUrl={`${window.location.origin}/custom-adventure-final-confirmation/${adventureData.requestNumber}`}
                />
              </Elements>
            ) : clientSecret === 'mock_client_secret_for_custom_adventure_development' ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">{t('developmentMode.title')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('developmentMode.description')}
                </p>
                <Button 
                  onClick={handlePaymentSuccess}
                  className="w-full bg-[#77A146] hover:bg-[#6a9139]"
                >
                  {t('developmentMode.simulateButton')}
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <p className="text-center text-gray-600 mt-4">{t('status.initializing')}</p>
              </div>
            )}
          </div>

          {/* Adventure Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900">{t('summary.title')}</h2>
              
              {/* Adventure Info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-[#77A146] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">{adventureData.adventureName}</p>
                    <p className="text-sm text-gray-600">{adventureData.businessName}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-[#77A146] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {format(new Date(adventureData.dateRange.startDate), 'MMM d, yyyy')} - {' '}
                      {format(new Date(adventureData.dateRange.endDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-[#77A146] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">{getTotalParticipants()} Participants</p>
                    <div className="text-sm text-gray-600">
                      {adventureData.participants.adults > 0 && <span>{adventureData.participants.adults} Adults</span>}
                      {adventureData.participants.children > 0 && <span>, {adventureData.participants.children} Children</span>}
                      {adventureData.participants.students > 0 && <span>, {adventureData.participants.students} Students</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('summary.priceBreakdown')}</h3>
                
                {adventureData.finalPrice.breakdown ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('summary.basePrice')}:</span>
                      <span className="text-gray-900">
                        ${adventureData.finalPrice.breakdown.basePrice.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('summary.options')}:</span>
                      <span className="text-gray-900">
                        ${adventureData.finalPrice.breakdown.options.toFixed(2)}
                      </span>
                    </div>
                    {adventureData.finalPrice.breakdown.taxes && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('summary.taxes')}:</span>
                        <span className="text-gray-900">
                          ${adventureData.finalPrice.breakdown.taxes.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span className="text-gray-900">{t('summary.total')}:</span>
                      <span className="text-[#77A146] text-lg">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: adventureData.finalPrice.currency
                        }).format(adventureData.finalPrice.total)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-900">{t('summary.total')}:</span>
                    <span className="text-[#77A146] text-xl">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: adventureData.finalPrice.currency
                      }).format(adventureData.finalPrice.total)}
                    </span>
                  </div>
                )}
              </div>

              {/* Request Number */}
              <div className="bg-gray-50 rounded-lg p-3 mt-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{t('summary.requestNumber')}</p>
                <p className="font-mono text-sm font-medium text-gray-900">{adventureData.requestNumber}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAdventurePayment;
